import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Compass, Plus, Trash2, ImageIcon, Loader2, Pencil, X } from "lucide-react";

async function compressImage(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  try {
    const url = URL.createObjectURL(file);
    const img = document.createElement("img");
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const max = 1200;
    let { width, height } = img;
    if (width > max || height > max) {
      const r = Math.min(max / width, max / height);
      width = Math.round(width * r); height = Math.round(height * r);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch (e) { return file; }
}

const blank = { title: "", description: "", link: "" };

function ItemManager({ kind, title, subtitle, icon: Icon, linkLabel }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | item object | null
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (file) {
      const u = URL.createObjectURL(file);
      setPreview(u);
      return () => URL.revokeObjectURL(u);
    }
    setPreview(editing && editing !== "new" ? editing.image : "");
  }, [file, editing]);

  const load = () => api.get(`/portal-items?kind=${kind}`).then(({ data }) => setItems(data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setEditing("new"); setForm(blank); setFile(null); };
  const openEdit = (it) => { setEditing(it); setForm({ title: it.title || "", description: it.description || "", link: it.link || "" }); setFile(null); };
  const cancel = () => { setEditing(null); setForm(blank); setFile(null); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("link", form.link);
      if (file) fd.append("image", await compressImage(file));
      if (editing === "new") {
        fd.append("kind", kind);
        await api.post("/portal-items", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.put(`/portal-items/${editing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Tile saved");
      cancel();
      load();
    } catch (e) { toast.error("Could not save tile"); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm("Delete this tile?")) return;
    try { await api.delete(`/portal-items/${it.id}`); toast.success("Tile deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <Card className="rounded-2xl border-navy-900/5 bg-white p-6 shadow-ambient" data-testid={`extras-card-${kind}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-navy-900">
          <Icon className="h-5 w-5 text-gold-600" />
          <div>
            <h3 className="font-serif text-xl font-bold">{title}</h3>
            <p className="text-sm text-navy-500">{subtitle}</p>
          </div>
        </div>
        {editing === null && (
          <Button data-testid={`extras-add-${kind}`} onClick={openNew} className="rounded-full bg-gold-500 text-white hover:bg-gold-600">
            <Plus className="mr-1.5 h-4 w-4" /> Add tile
          </Button>
        )}
      </div>

      {editing !== null && (
        <div className="mt-5 rounded-xl border border-gold-100 bg-gold-50/40 p-5" data-testid={`extras-editor-${kind}`}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Title</Label>
              <Input data-testid={`extras-title-${kind}`} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 focus-visible:ring-gold-500" placeholder="e.g. New meditation added" />
            </div>
            <div>
              <Label>{linkLabel || "Link (optional)"}</Label>
              <Input data-testid={`extras-link-${kind}`} value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-1 font-mono text-sm focus-visible:ring-gold-500" placeholder="https://..." />
            </div>
          </div>
          <div className="mt-4">
            <Label>Information</Label>
            <textarea data-testid={`extras-desc-${kind}`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500" placeholder="Short description shown on the tile" />
          </div>
          <div className="mt-4">
            <Label>Thumbnail image</Label>
            <label data-testid={`extras-image-drop-${kind}`} className="mt-1 flex cursor-pointer items-center gap-4">
              <div className="h-20 w-32 overflow-hidden rounded-lg border border-navy-100 bg-white">
                {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><ImageIcon className="h-6 w-6 text-gold-300" /></div>}
              </div>
              <span className="text-sm font-medium text-gold-700">Click to {preview ? "replace" : "upload"} image</span>
              <input type="file" accept="image/*" className="hidden" data-testid={`extras-image-input-${kind}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setFile(f); }} />
            </label>
          </div>
          <div className="mt-5 flex gap-3">
            <Button data-testid={`extras-save-${kind}`} onClick={save} disabled={saving} className="rounded-full bg-gold-500 text-white hover:bg-gold-600">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save tile
            </Button>
            <Button variant="ghost" onClick={cancel} className="rounded-full text-navy-500"><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && editing === null && (
          <p className="text-sm text-navy-400">No tiles yet — click "Add tile" to create one.</p>
        )}
        {items.map((it) => (
          <div key={it.id} data-testid={`extras-item-${it.id}`} className="overflow-hidden rounded-xl border border-navy-900/5 bg-white shadow-ambient">
            <div className="h-24 w-full bg-navy-50">
              {it.image ? <img src={it.image} alt={it.title} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Icon className="h-7 w-7 text-navy-200" /></div>}
            </div>
            <div className="p-3">
              <h4 className="truncate font-semibold text-navy-900">{it.title}</h4>
              {it.description && <p className="mt-0.5 line-clamp-2 text-xs text-navy-500">{it.description}</p>}
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" data-testid={`extras-edit-${it.id}`} onClick={() => openEdit(it)} className="h-8 rounded-full border-gold-200 text-gold-700 hover:bg-gold-50"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button size="sm" variant="ghost" data-testid={`extras-delete-${it.id}`} onClick={() => remove(it)} className="h-8 rounded-full text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function PortalExtras() {
  return (
    <div data-testid="portal-extras-page">
      <PageHeader title="What's New & Discover" subtitle="Manage the tiles clients see on their dashboard and in the Discover More tab." />
      <div className="space-y-8">
        <ItemManager kind="whatsnew" title="What's New (Dashboard)" subtitle="Small update tiles shown on the client dashboard." icon={Sparkles} linkLabel="Link (optional)" />
        <ItemManager kind="discover" title="Discover More (Products)" subtitle="Product tiles like RCC Hive & iExtol — thumbnail, info and website link." icon={Compass} linkLabel="Product website link" />
      </div>
    </div>
  );
}
